import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ApiRequestError, get, post } from '@/lib/api';
import type { RatioOverrideInfo } from '@/lib/canvasSettings';
import type { FlexibleAgentRequestPayload } from '@/lib/modelRequestPayload';

export interface PipelineStage {
  id: string;
  name: string;
  type: string;
  status: 'pending' | 'in_progress' | 'awaiting_approval' | 'rejected' | 'completed' | 'failed';
  content?: Record<string, unknown>;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface PipelineCharacter {
  id: string;
  name: string;
  description?: string;
  image?: string;
  attributes?: Record<string, unknown>;
}

export interface PipelineScene {
  id: string;
  order: number;
  description: string;
  image?: string;
  audio?: string;
}

export interface ModelSchemaProperty {
  type: 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object';
  description?: string;
  default?: unknown;
  enum?: (string | number | boolean)[];
  maximum?: number;
  minimum?: number;
  step?: number;
  'x-ui-component'?: string;
  'x-placeholder'?: string;
  'x-hint'?: string;
  'x-rows'?: number;
  'x-hidden'?: boolean;
  'x-enum'?: string[];
  items?: {
    type: string;
    properties?: Record<string, ModelSchemaProperty>;
    'x-order-properties'?: string[];
  };
  maxItems?: number;
  minItems?: number;
  format?: string;
}

export interface ModelRequestSchema {
  type: 'object';
  required?: string[];
  properties: Record<string, ModelSchemaProperty>;
  'x-order-properties'?: string[];
  if?: Record<string, unknown>;
  then?: Record<string, unknown>;
  else?: Record<string, unknown>;
  allOf?: Array<Record<string, unknown>>;
}

export interface ModelEndpoint {
  model_name: string;
  model_id: string;
  name: string;
  endpoint: string;
  provider: string;
  model_type: string;
  specialty: string;
  status: string;
  type?: string;
  description?: string;
  pricing?: number;
  discount?: number;
  discounted_points?: number;
  request_schema?: ModelRequestSchema;
  id?: number;
}

export interface ModelEndpointGroup {
  [key: string]: ModelEndpoint[];
}

export interface ModelsResponse {
  models: ModelEndpoint[];
  modelsByType: ModelEndpointGroup;
}

export interface FlexibleAgentProcessTaskItem {
  task_id?: string;
  success?: boolean;
  error_code?: string | number;
  error?: string;
  code?: string | number;
  message?: string;
}

export interface FlexibleAgentProcessApiResponse {
  task_id?: string;
  task_ids?: string[];
  failed_count?: number;
  pending_billing_count?: number;
  quantity?: number;
  ratio_override?: RatioOverrideInfo;
  success_count?: number;
  tasks?: FlexibleAgentProcessTaskItem[];
}

export interface FlexibleAgentResponse {
  task_id?: string;
  task_ids: string[];
  failed_count?: number;
  pending_billing_count?: number;
  quantity?: number;
  ratio_override?: RatioOverrideInfo;
  success_count?: number;
  tasks?: FlexibleAgentProcessTaskItem[];
}

export interface BatchQueryTaskSuccessItem {
  task_id: string;
  success: true;
  code: number;
  data: unknown;
  message?: string;
  retryable?: boolean;
  billing?: {
    billing_status?: string;
    task_status?: string;
    required_points?: number;
    consumed_points?: number;
  };
  finalize_status?: string;
  finalize_error?: string;
}

export interface BatchQueryTaskFailureItem {
  task_id: string;
  success: false;
  error_code: string;
  error: string;
  code?: number;
  message?: string;
  data?: unknown;
  retryable?: boolean;
}

export type BatchQueryTaskItem = BatchQueryTaskSuccessItem | BatchQueryTaskFailureItem;

export interface BatchQueryTasksResponse {
  code: number;
  message: string;
  data: {
    tasks: BatchQueryTaskItem[];
    success_count: number;
    failed_count: number;
    internal_error_count: number;
    has_system_error: boolean;
  };
}

interface BatchQueryTasksErrorPayload {
  code?: string | number;
  message: string;
  status?: number;
  data?: unknown;
}

function extractFlexibleAgentTaskIds(data: FlexibleAgentProcessApiResponse): string[] {
  const taskIdsFromList = Array.isArray(data.task_ids) ? data.task_ids : [];
  const taskIdsFromTasks = Array.isArray(data.tasks)
    ? data.tasks
        .map((item) => item.task_id)
        .filter((taskId): taskId is string => typeof taskId === 'string' && taskId.length > 0)
    : [];
  const fallbackTaskId = typeof data.task_id === 'string' && data.task_id.length > 0
    ? [data.task_id]
    : [];

  return Array.from(new Set([
    ...taskIdsFromList,
    ...taskIdsFromTasks,
    ...fallbackTaskId,
  ]));
}

export interface CalculatePipelinePricingRequest {
  model_id?: string;
  request_type?: string;
  canvas_id?: string;
  node_id?: string;
  prompt?: string;
  style_id?: string;
  node_type?: string;
  images?: string[];
  videos?: string[];
  audio?: string[];
  assets?: string[];
  action?: {
    type: string;
    mask?: string;
    top?: number;
    right?: number;
    bottom?: number;
      left?: number;
    };
  params?: Record<string, unknown>;
}

export interface CalculatePipelinePricingResponse {
  model_id: string;
  price: number;
  discount?: number;
  discounted_points?: number;
  required_points: number;
  formulas: string[];
}

export interface CanvasNodePosition {
  canvas_id: string;
  id: string;
  position: {
    x: number;
    y: number;
  };
}

export interface UpdateCanvasNodesPayload {
  actions: Array<{
    action: 'update' | 'create';
    nodes: CanvasNodePosition[];
  }>;
}

export interface Pipeline {
  id: string;
  name: string;
  projectId: string;
  status: 'pending' | 'in_progress' | 'awaiting_approval' | 'rejected' | 'completed' | 'failed';
  currentStage?: string;
  stages: PipelineStage[];
  characters: PipelineCharacter[];
  scenes: PipelineScene[];
  progress: number;
  createdAt: string;
  updatedAt: string;
}

interface PipelineState {
  items: Pipeline[];
  currentPipeline: Pipeline | null;
  loading: boolean;
  error: string | null;
  models: ModelEndpoint[];
  modelsLoading: boolean;
}

const initialState: PipelineState = {
  items: [],
  currentPipeline: null,
  loading: false,
  error: null,
  models: [],
  modelsLoading: false,
};

/**
 * Pipeline Service - 视频生成流水线
 * 提供流水线创建、管理、阶段控制等功能
 * Base URL: /api/v1/pipeline, /api/v1/project
 */

/**
 * 获取项目的所有流水线
 * GET /api/v1/project/:project_id/pipelines
 * @param projectId - Project ID
 * @returns Pipeline数组
 */
export const fetchPipelines = createAsyncThunk(
  'pipeline/fetchPipelines',
  async (projectId: string) => {
    const data = await get<{ success: boolean; data?: Pipeline[]; error?: string }>(`/api/v1/project/${projectId}/pipelines`);
    if (data.success && data.data) {
      return data.data;
    }
    throw new Error(data.error || 'Failed to fetch pipelines');
  }
);

/**
 * 获取流水线详情
 * GET /api/v1/pipeline/:id
 * @param id - Pipeline ID
 * @returns Pipeline详情
 */
export const fetchPipelineDetail = createAsyncThunk(
  'pipeline/fetchPipelineDetail',
  async (id: string) => {
    const data = await get<{ success: boolean; data?: Pipeline; error?: string }>(`/api/v1/pipeline/${id}`);
    if (data.success && data.data) {
      return data.data;
    }
    throw new Error(data.error || 'Failed to fetch pipeline');
  }
);

/**
 * 创建流水线
 * POST /api/v1/pipeline/create
 * @param data - 流水线数据（name: 名称, projectId: 项目ID）
 * @returns 创建的Pipeline
 */
export const createPipeline = createAsyncThunk(
  'pipeline/createPipeline',
  async (data: { name: string; projectId: string }) => {
    const result = await post<{ success: boolean; data?: Pipeline; error?: string }>('/api/v1/pipeline/create', data);
    if (result.success && result.data) {
      return result.data;
    }
    throw new Error(result.error || 'Failed to create pipeline');
  }
);

/**
 * 获取流水线所有阶段
 * GET /api/v1/pipeline/:id/stages
 * @param id - Pipeline ID
 * @returns 阶段列表
 */
export const fetchPipelineStages = createAsyncThunk(
  'pipeline/fetchStages',
  async (id: string) => {
    const data = await get<{ success: boolean; data?: PipelineStage[]; error?: string }>(`/api/v1/pipeline/${id}/stages`);
    if (data.success && data.data) {
      return { pipelineId: id, stages: data.data };
    }
    throw new Error(data.error || 'Failed to fetch stages');
  }
);

/**
 * 获取渲染进度
 * GET /api/v1/pipeline/:id/progress
 * @param id - Pipeline ID
 * @returns 进度信息
 */
export const fetchPipelineProgress = createAsyncThunk(
  'pipeline/fetchProgress',
  async (id: string) => {
    const data = await get<{ success: boolean; data?: { progress: number; status: string }; error?: string }>(`/api/v1/pipeline/${id}/progress`);
    if (data.success && data.data) {
      return { id, progress: data.data.progress, status: data.data.status };
    }
    throw new Error(data.error || 'Failed to fetch progress');
  }
);

/**
 * 推进到下一阶段
 * POST /api/v1/pipeline/:id/advance
 * @param id - Pipeline ID
 * @returns 更新后的Pipeline
 */
export const advancePipeline = createAsyncThunk(
  'pipeline/advancePipeline',
  async (id: string) => {
    const data = await post<{ success: boolean; data?: Pipeline; error?: string }>(`/api/v1/pipeline/${id}/advance`);
    if (data.success && data.data) {
      return data.data;
    }
    throw new Error(data.error || 'Failed to advance pipeline');
  }
);

/**
 * 拒绝当前阶段
 * POST /api/v1/pipeline/:id/reject
 * @param id - Pipeline ID
 * @returns 更新后的Pipeline
 */
export const rejectPipeline = createAsyncThunk(
  'pipeline/rejectPipeline',
  async (id: string) => {
    const data = await post<{ success: boolean; data?: Pipeline; error?: string }>(`/api/v1/pipeline/${id}/reject`);
    if (data.success && data.data) {
      return data.data;
    }
    throw new Error(data.error || 'Failed to reject pipeline');
  }
);

/**
 * 重新生成阶段
 * POST /api/v1/pipeline/:id/stages/:stage_id/regenerate
 * @param pipelineId - Pipeline ID
 * @param stageId - Stage ID
 * @returns 更新后的Pipeline
 */
export const regenerateStage = createAsyncThunk(
  'pipeline/regenerateStage',
  async ({ pipelineId, stageId }: { pipelineId: string; stageId: string }) => {
    const data = await post<{ success: boolean; data?: Pipeline; error?: string }>(`/api/v1/pipeline/${pipelineId}/stages/${stageId}/regenerate`);
    if (data.success && data.data) {
      return data.data;
    }
    throw new Error(data.error || 'Failed to regenerate stage');
  }
);

/**
 * 更新阶段内容
 * POST /api/v1/pipeline/:id/stages/:stage_id
 * @param pipelineId - Pipeline ID
 * @param stageId - Stage ID
 * @param content - 阶段内容
 * @returns 更新后的Stage
 */
export const updateStage = createAsyncThunk(
  'pipeline/updateStage',
  async ({ pipelineId, stageId, content }: { pipelineId: string; stageId: string; content: Record<string, unknown> }) => {
    const data = await post<{ success: boolean; data?: PipelineStage; error?: string }>(`/api/v1/pipeline/${pipelineId}/stages/${stageId}`, { content });
    if (data.success && data.data) {
      return { pipelineId, stage: data.data };
    }
    throw new Error(data.error || 'Failed to update stage');
  }
);

/**
 * 获取流水线日志
 * GET /api/v1/pipeline/:id/logs
 * @param id - Pipeline ID
 * @returns 日志数组
 */
export const fetchPipelineLogs = createAsyncThunk(
  'pipeline/fetchLogs',
  async (id: string) => {
    const data = await get<{ success: boolean; data?: unknown[]; error?: string }>(`/api/v1/pipeline/${id}/logs`);
    if (data.success && data.data) {
      return { id, logs: data.data };
    }
    throw new Error(data.error || 'Failed to fetch logs');
  }
);

/**
 * 获取流水线角色列表
 * GET /api/v1/pipeline/:id/characters
 * @param pipelineId - Pipeline ID
 * @returns 角色数组
 */
export const fetchPipelineCharacters = createAsyncThunk(
  'pipeline/fetchCharacters',
  async (pipelineId: string) => {
    const data = await get<{ success: boolean; data?: PipelineCharacter[]; error?: string }>(`/api/v1/pipeline/${pipelineId}/characters`);
    if (data.success && data.data) {
      return { pipelineId, characters: data.data };
    }
    throw new Error(data.error || 'Failed to fetch characters');
  }
);

/**
 * 添加角色到流水线
 * POST /api/v1/pipeline/:id/characters
 * @param pipelineId - Pipeline ID
 * @param data - 角色数据
 * @returns 添加的角色
 */
export const addPipelineCharacter = createAsyncThunk(
  'pipeline/addCharacter',
  async ({ pipelineId, data: charData }: { pipelineId: string; data: { name: string; description?: string } }) => {
    const data = await post<{ success: boolean; data?: PipelineCharacter; error?: string }>(`/api/v1/pipeline/${pipelineId}/characters`, charData);
    if (data.success && data.data) {
      return { pipelineId, character: data.data };
    }
    throw new Error(data.error || 'Failed to add character');
  }
);

/**
 * 更新流水线角色
 * POST /api/v1/pipeline/:id/characters/:char_id
 * @param pipelineId - Pipeline ID
 * @param charId - Character ID
 * @param data - 更新数据
 * @returns 更新后的角色
 */
export const updatePipelineCharacter = createAsyncThunk(
  'pipeline/updateCharacter',
  async ({ pipelineId, charId, data: charData }: { pipelineId: string; charId: string; data: Partial<PipelineCharacter> }) => {
    const result = await post<{ success: boolean; data?: PipelineCharacter; error?: string }>(`/api/v1/pipeline/${pipelineId}/characters/${charId}`, charData);
    if (result.success && result.data) {
      return { pipelineId, character: result.data };
    }
    throw new Error(result.error || 'Failed to update character');
  }
);

/**
 * 删除流水线角色
 * POST /api/v1/pipeline/:id/characters/:char_id/delete
 * @param pipelineId - Pipeline ID
 * @param charId - Character ID
 * @returns 删除的角色ID
 */
export const deletePipelineCharacter = createAsyncThunk(
  'pipeline/deleteCharacter',
  async ({ pipelineId, charId }: { pipelineId: string; charId: string }) => {
    await post(`/api/v1/pipeline/${pipelineId}/characters/${charId}/delete`);
    return { pipelineId, charId };
  }
);

/**
 * 获取流水线场景列表
 * GET /api/v1/pipeline/:id/scenes
 * @param pipelineId - Pipeline ID
 * @returns 场景数组
 */
export const fetchPipelineScenes = createAsyncThunk(
  'pipeline/fetchScenes',
  async (pipelineId: string) => {
    const data = await get<{ success: boolean; data?: PipelineScene[]; error?: string }>(`/api/v1/pipeline/${pipelineId}/scenes`);
    if (data.success && data.data) {
      return { pipelineId, scenes: data.data };
    }
    throw new Error(data.error || 'Failed to fetch scenes');
  }
);

/**
 * 添加场景到流水线
 * POST /api/v1/pipeline/:id/scenes
 * @param pipelineId - Pipeline ID
 * @param data - 场景数据
 * @returns 添加的场景
 */
export const addPipelineScene = createAsyncThunk(
  'pipeline/addScene',
  async ({ pipelineId, data: sceneData }: { pipelineId: string; data: { name: string; content?: Record<string, unknown> } }) => {
    const data = await post<{ success: boolean; data?: PipelineScene; error?: string }>(`/api/v1/pipeline/${pipelineId}/scenes`, sceneData);
    if (data.success && data.data) {
      return { pipelineId, scene: data.data };
    }
    throw new Error(data.error || 'Failed to add scene');
  }
);

/**
 * 更新流水线场景
 * POST /api/v1/pipeline/:id/scenes/:scene_id
 * @param pipelineId - Pipeline ID
 * @param sceneId - Scene ID
 * @param data - 更新数据
 * @returns 更新后的场景
 */
export const updatePipelineScene = createAsyncThunk(
  'pipeline/updateScene',
  async ({ pipelineId, sceneId, data: sceneData }: { pipelineId: string; sceneId: string; data: Partial<PipelineScene> }) => {
    const result = await post<{ success: boolean; data?: PipelineScene; error?: string }>(`/api/v1/pipeline/${pipelineId}/scenes/${sceneId}`, sceneData);
    if (result.success && result.data) {
      return { pipelineId, scene: result.data };
    }
    throw new Error(result.error || 'Failed to update scene');
  }
);

/**
 * 删除流水线场景
 * POST /api/v1/pipeline/:id/scenes/:scene_id/delete
 * @param pipelineId - Pipeline ID
 * @param sceneId - Scene ID
 * @returns 删除的场景ID
 */
export const deletePipelineScene = createAsyncThunk(
  'pipeline/deleteScene',
  async ({ pipelineId, sceneId }: { pipelineId: string; sceneId: string }) => {
    await post(`/api/v1/pipeline/${pipelineId}/scenes/${sceneId}/delete`);
    return { pipelineId, sceneId };
  }
);

/**
 * 处理灵活智能体请求
 * POST /api/pipeline/agents/flexible/batch-process
 * @param params - 请求参数
 * @returns 处理结果
 */
export const processFlexibleAgent = createAsyncThunk(
  'pipeline/processFlexibleAgent',
  async (params: FlexibleAgentRequestPayload, { rejectWithValue, signal }) => {
    try {
      const data = await post<{ code: number; message: string; data: FlexibleAgentProcessApiResponse }>(
        '/api/pipeline/agents/flexible/batch-process',
        params,
        { silent: true, signal }
      );
      const taskIds = extractFlexibleAgentTaskIds(data.data);
      return {
        task_id: taskIds[0],
        task_ids: taskIds,
        failed_count: data.data.failed_count,
        pending_billing_count: data.data.pending_billing_count,
        quantity: data.data.quantity,
        ratio_override: data.data.ratio_override,
        success_count: data.data.success_count,
        tasks: data.data.tasks,
      } satisfies FlexibleAgentResponse;
    } catch (error) {
      if (error instanceof ApiRequestError) {
        return rejectWithValue({
          code: error.code,
          message: error.message,
          status: error.status,
          data: error.data,
        });
      }

      throw error;
    }
  }
);

/**
 * 批量查询任务结果
 * POST /api/pipeline/agents/flexible/batch-query
 * @param params - 查询参数
 * @param params.task_ids - 任务 ID 数组，最多 9 个
 * @returns 任务结果
 */
export const batchQueryTasks = createAsyncThunk(
  'pipeline/batchQueryTasks',
  async (params: { task_ids: string[] }, { rejectWithValue, signal }) => {
    try {
      return await post<BatchQueryTasksResponse>('/api/pipeline/agents/flexible/batch-query', params, { silent: true, signal });
    } catch (error) {
      if (error instanceof ApiRequestError) {
        return rejectWithValue({
          code: error.code,
          message: error.message,
          status: error.status,
          data: error.data,
        } satisfies BatchQueryTasksErrorPayload);
      }

      throw error;
    }
  }
);

export const calculatePipelinePricing = createAsyncThunk(
  'pipeline/calculatePipelinePricing',
  async (params: CalculatePipelinePricingRequest, { signal }) => {
    const data = await post<{ code: number; message: string; data: CalculatePipelinePricingResponse }>(
      '/api/pipeline/pricing/calculate',
      params,
      { silent: true, signal }
    );
    return data.data;
  }
);

/**
 * 获取所有模型列表
 * GET /api/v1/models/endpoint
 * @returns 模型列表
 */
export const fetchModelEndpoints = createAsyncThunk(
  'pipeline/fetchModelEndpoints',
  async () => {
    const data = await get<{ code: string; message: string; data?: { models?: ModelEndpoint[] }; models?: ModelEndpoint[]; total?: number }>('/api/pipeline/models/endpoint');
    const models = data.data?.models ?? data.models;
    if (!models) throw new Error(data.message || 'Failed to fetch model endpoints');
    return models.map((m) => ({ ...m, pricing: m.pricing ?? 1 }));
  }
);

/**
 * 更新画布节点位置
 * POST /api/v1/canvas/nodes/batch
 * @param data - 节点更新数据
 * @returns 更新结果
 */
export const updateCanvasNodes = createAsyncThunk(
  'pipeline/updateCanvasNodes',
  async (data: UpdateCanvasNodesPayload) => {
    const result = await post<{ code: string; message: string }>('/api/v1/canvas/nodes/batch', data);
    if (result.code === 'SUCCESS') {
      return result;
    }
    throw new Error(result.message || 'Failed to update canvas nodes');
  }
);

/**
 * 创建画布节点
 * POST /api/v1/canvas/nodes/batch
 * @param data - 节点创建数据
 * @returns 创建结果
 */
export const createCanvasNodes = createAsyncThunk(
  'pipeline/createCanvasNodes',
  async (data: UpdateCanvasNodesPayload) => {
    const result = await post<{ code: string; message: string }>('/api/v1/canvas/nodes/batch', data);
    if (result.code === 'SUCCESS') {
      return result;
    }
    throw new Error(result.message || 'Failed to create canvas nodes');
  }
);

const pipelineSlice = createSlice({
  name: 'pipeline',
  initialState,
  reducers: {
    setCurrentPipeline: (state, action: PayloadAction<Pipeline | null>) => {
      state.currentPipeline = action.payload;
    },
    setPipelineLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPipelineError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearPipelineError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPipelines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPipelines.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchPipelines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch pipelines';
      })
      .addCase(fetchPipelineDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPipelineDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPipeline = action.payload;
      })
      .addCase(fetchPipelineDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch pipeline';
      })
      .addCase(createPipeline.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.currentPipeline = action.payload;
      })
      .addCase(advancePipeline.fulfilled, (state, action) => {
        if (state.currentPipeline?.id === action.payload.id) {
          state.currentPipeline = action.payload;
        }
      })
      .addCase(rejectPipeline.fulfilled, (state, action) => {
        if (state.currentPipeline?.id === action.payload.id) {
          state.currentPipeline = action.payload;
        }
      })
      .addCase(regenerateStage.fulfilled, (state, action) => {
        if (state.currentPipeline?.id === action.payload.id) {
          state.currentPipeline = action.payload;
        }
      })
      .addCase(fetchModelEndpoints.pending, (state) => {
        state.modelsLoading = true;
      })
      .addCase(fetchModelEndpoints.fulfilled, (state, action) => {
        state.models = action.payload;
        state.modelsLoading = false;
      })
      .addCase(fetchModelEndpoints.rejected, (state) => {
        state.modelsLoading = false;
      });
  },
});

export const { setCurrentPipeline, setPipelineLoading, setPipelineError, clearPipelineError } = pipelineSlice.actions;
export default pipelineSlice.reducer;
